import React, { useEffect, useState } from 'react';
import { ChartLineUp, Brain, WarningCircle, Cpu } from '@phosphor-icons/react';
import { authService } from '../../../services/auth.service';

interface UsageDetailPanelProps {
  email: string;
}

export const UsageDetailPanel: React.FC<UsageDetailPanelProps> = ({ email }) => {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchUsage = async () => {
      try {
        setIsLoading(true);
        const result = await authService.getUserUsage(email);
        if (isMounted) {
          setData(result);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Failed to load token usage details');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUsage();
    return () => { isMounted = false; };
  }, [email]);

  if (isLoading) {
    return (
      <div className="p-6 bg-slate-50/50 flex items-center justify-center min-h-[160px]">
        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-rose-50/50 flex flex-col items-center justify-center min-h-[160px] text-rose-600">
        <WarningCircle size={32} weight="fill" className="mb-2" />
        <p className="text-sm font-semibold">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);
  const models = Object.entries(data.by_model || {});

  return (
    <div className="p-5 bg-slate-50/70 border-t border-slate-100">
      {/* Single Unified Container */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
        
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-xl">
              <Cpu size={20} weight="bold" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 font-outfit">Detailed Token Usage Analytics</h3>
              <p className="text-xs text-slate-400">Model breakdown & recent daily activity for {email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-xs font-semibold">
            <span className="text-slate-500">
              Total Calls: <strong className="text-slate-900 font-bold">{formatNumber(data.total_calls || 0)}</strong>
            </span>
            <span className="px-3 py-1 bg-orange-50 text-orange-700 font-bold rounded-full border border-orange-200/50">
              {formatNumber(data.total_tokens || 0)} Total Tokens
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Models Breakdown */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Brain size={16} className="text-orange-500" />
              Model Breakdown
            </h4>
            
            {models.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                No AI token usage recorded for this user yet.
              </div>
            ) : (
              <div className="space-y-4">
                {models.map(([modelName, stats]: [string, any]) => {
                  const percent = data.total_tokens > 0 
                    ? Math.round((stats.total_tokens / data.total_tokens) * 100) 
                    : 0;
                  
                  const isClaude = modelName.toLowerCase().includes('claude');

                  return (
                    <div key={modelName} className="p-3 bg-slate-50/80 rounded-xl border border-slate-100 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-800 truncate mr-2 flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                            isClaude ? 'bg-amber-100 text-amber-800' : 'bg-slate-900 text-white'
                          }`}>
                            {stats.provider}
                          </span>
                          {modelName}
                        </span>
                        <span className="font-bold text-slate-900">{formatNumber(stats.total_tokens)} tokens</span>
                      </div>
                      
                      <div className="w-full bg-slate-200/70 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${isClaude ? 'bg-amber-500' : 'bg-orange-500'}`} 
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                      
                      <div className="flex justify-between text-[11px] text-slate-500 font-medium pt-0.5">
                        <span>Input: <strong>{formatNumber(stats.prompt_tokens)}</strong> | Output: <strong>{formatNumber(stats.completion_tokens)}</strong></span>
                        <span className="text-slate-600 font-bold">{stats.call_count} requests</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Daily Usage Activity */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <ChartLineUp size={16} className="text-orange-500" />
              Recent Daily Token Activity
            </h4>
            
            {!data.daily || data.daily.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                No recent token activity logs available.
              </div>
            ) : (
              <div className="space-y-3">
                {data.daily.slice(0, 6).map((day: any) => {
                  const maxTokens = Math.max(...data.daily.map((d: any) => d.total_tokens));
                  const percent = maxTokens > 0 ? Math.round((day.total_tokens / maxTokens) * 100) : 0;
                  
                  const dateObj = new Date(day.date);
                  const isToday = new Date().toISOString().split('T')[0] === day.date;
                  
                  return (
                    <div key={day.date} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                      <div className="w-20 text-[11px] font-bold text-slate-600 text-right shrink-0">
                        {isToday ? (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px]">Today</span>
                        ) : (
                          dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        )}
                      </div>
                      
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden flex items-center">
                           <div 
                             className="bg-gradient-to-r from-amber-500 to-orange-500 h-full rounded-full min-w-[4px]" 
                             style={{ width: `${percent}%` }}
                           ></div>
                        </div>
                        <div className="w-20 text-right text-xs font-bold text-slate-800 shrink-0">
                          {formatNumber(day.total_tokens)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
